<template>
  <div class="offer-highlights">
    <ProductContentDetails
      v-if="displayOfferUsps || displayProductUsps"
      :offer="offer"
      :render-item-if="(product) => updateUsps(product)?.length > 0"
    >
      <template #default="props">
        <div
          class="offer-highlights__row"
          :class="{
            'offer-highlights__row--half': hasSingleVideo(
              props.product?.media || props.offer?.media
            ),
          }"
        >
          <div class="offer-highlights__list-column">
            <BulletPointList
              :list="updateUsps(props.product)"
              itemprop="description"
            />
            <div class="offer-highlights__links">
              <KompalisteLink
                :eans="props.product.eans"
                class="offer-highlights__kompaliste-link"
              />
              <KameraLink
                :offer="props.offer"
                class="offer-highlights__kamera-link"
              />
            </div>
          </div>
          <OfferHighlightVideos
            :media="props.product.media || props.offer.media"
            class="offer-highlights__list-column"
          />
        </div>

        <!-- If the product itself is a bundle, the description is shown on "product-details" -->
        <ContentToggle
          v-if="props.product.description && !props.product.bundle"
          no-border
          class="offer-highlights__content-toggle"
        >
          <article
            class="offer-highlights__description unstyled-html"
            :data-testid="`bundle-description-${props.product.family.slug}`"
            v-html="props.product.description"
          />
        </ContentToggle>
      </template>
    </ProductContentDetails>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { offerHasOwnUsps, offerHasProductUsps } from '~/lib/goliath/offer-usps';
import BulletPointList from '~/components/bullet-point-list.vue';
import ProductContentDetails from '~/components/product-detail/tabs/product-content-details.vue';
import ContentToggle from '~/components/content-toggle.vue';
import KompalisteLink from '~/components/kompaliste-link.vue';
import OfferHighlightVideos from '~/components/product-detail/tabs/offer-highlight-videos.vue';
import type { Attachment } from '~/lib/goliath/goliath.types';
import type { Offer, OfferProduct } from '~/lib/goliath/offer-by-slugs';
import KameraLink from '~/components/kamera-link.vue';

const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
});

const displayOfferUsps = computed(() => props.offer && offerHasOwnUsps(props.offer));

const displayProductUsps = computed(
  () => !displayOfferUsps.value && props.offer && offerHasProductUsps(props.offer)
);

const getVideos = (media: Attachment[]) => {
  if (!media) return [];
  return media.filter((mediaObject) => mediaObject.remote && mediaObject.type === 'VIDEO');
};
const hasSingleVideo = (media: Attachment[]) => getVideos(media).length === 1;

const updateUsps = (product: OfferProduct | undefined) => {
  if (!product) return [];
  const infoMessages = {
    wifiCompatible:
      '**Wichtiger Hinweis:** Das Gerät wird über WLAN eingebunden, es wird keine weitere Hardware benötigt',
    compatibilityRequirements:
      '**Wichtiger Hinweis:** Für die Einbindung des Gerätes ist weitere Hardware z. B. eine Bridge/Gateway oder eine Zentrale nötig.',
  };
  const isWifiCompatible = product.tags?.includes('i22-wifi-compatible');
  const isSmartHomeProduct = product.family?.rootCategories?.some(
    (category) => category?.slug === 'smart-home'
  );
  if (!isSmartHomeProduct) return product.usps;
  return isWifiCompatible
    ? [...product.usps, infoMessages.wifiCompatible]
    : [...product.usps, infoMessages.compatibilityRequirements];
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
@use '@i22/td-longtail-vue-components/dist/styles/pattern/text';

.offer-highlights {
  &__row {
    display: grid;
    grid-gap: $grid-base;

    &--half {
      @include mq($mq-md) {
        grid-template-columns: 1fr 1fr;
      }
    }
  }

  &__list-column {
    :deep(.list__element) a {
      @extend .link;

      &[target='_blank'] {
        @extend .link--external;
      }
    }
  }

  &__content-toggle {
    margin: 4 * $grid-base 0;
    padding-left: 0;

    @include mq($mq-md) {
      margin: 6 * $grid-base 0;
    }
  }

  &__description {
    columns: 456px 3;
    column-gap: 48px;

    @include mq($mq-sm) {
      margin-bottom: 4 * $grid-base;
    }
    :deep(p) {
      line-height: 1.6;
    }
    :deep(p + h2, p + h3) {
      margin-top: 3rem;
    }
  }
  &__links {
    @include mq($mq-sm) {
      display: flex;
      justify-content: stretch;
    }
  }

  &__kompaliste-link,
  &__kamera-link {
    margin-left: 1.5 * $grid-base;
  }
}
</style>
